<!--
  WorldMapHero — accurate dotted world map with connection arcs converging on the US.
  Illustrates "hire international talent": arcs draw from global hubs to a US node.

  The dotted map is a static asset (wwwroot/images/world-dots.svg), generated once at
  build time with the `dotted-map` library (height 60, diagonal grid) — so the app
  ships zero extra runtime dependencies and pays no per-dot DOM cost. City pin
  coordinates were produced from the same map (getPin) so arc endpoints land exactly
  on their real lon/lat. Arcs/nodes are a small CSS-animated SVG overlay sharing the
  map's "0 0 119 60" coordinate space. Pure SVG + CSS, reduced-motion aware.
-->
<template>
  <div class="wm-hero" aria-hidden="true">
    <div class="wm-map">
      <img class="wm-dots-img" :src="mapSrc" alt="" draggable="false" />
      <svg class="wm-overlay" viewBox="0 -6 158 112" preserveAspectRatio="xMidYMid meet" fill="none">
        <g class="wm-arcs">
          <path
            v-for="(a, i) in arcs"
            :key="'a' + i"
            :d="a.d"
            pathLength="1"
            class="wm-arc"
            :style="{ animationDelay: a.delay + 's' }"
          />
        </g>
        <g class="wm-nodes">
          <template v-for="(n, i) in nodes" :key="'n' + i">
            <circle v-if="n.hub" :cx="n.x" :cy="n.y" r="1.1" class="wm-ring wm-ring--hub" :style="{ animationDelay: n.delay + 's' }" />
            <circle :cx="n.x" :cy="n.y" :r="n.hub ? 1.2 : 0.65" class="wm-node" :class="{ 'wm-node--hub': n.hub }" />
          </template>
        </g>
      </svg>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

// Pins in the dotted map's "0 0 158 100" space (dotted-map getPin, height 100,
// region lat -56..83 so the northern landmasses are included).
// Talent flows into four US destination hubs from cities worldwide.
const HUBS: Record<string, [number, number]> = {
  NYC: [46, 51.1],
  SF: [25, 52.83],
  Chicago: [40.5, 50.23],
  Austin: [36, 56.29],
};
// [originX, originY, hubKey] — each incoming line and its US destination
const LINKS: [number, number, string][] = [
  [79, 44.17, 'NYC'],       // London
  [93, 56.29, 'NYC'],       // Cairo
  [58, 80.54, 'NYC'],       // São Paulo
  [80.5, 67.55, 'Chicago'], // Lagos
  [95.5, 71.01, 'Chicago'], // Nairobi
  [111, 61.49, 'Chicago'],  // Mumbai
  [113, 64.95, 'Chicago'],  // Bengaluru
  [132.5, 64.09, 'SF'],     // Manila
  [133, 56.29, 'SF'],       // Shanghai
  [135, 52.83, 'SF'],       // Seoul
  [126.5, 72.75, 'SF'],     // Jakarta
  [35, 61.49, 'Austin'],    // Mexico City
  [46, 68.42, 'Austin'],    // Bogotá
];

export default defineComponent({
  name: 'WorldMapHero',
  setup() {
    // SSR/prerender (no window) emits the literal __PATHBASE__ token; EmployerLandingPrerender
    // replaces it with Request.PathBase ('' local, '/jobs' deployed) so the very first paint
    // requests the correct URL — no root-relative 404 + broken-image flash before mount.
    const isClient = typeof window !== 'undefined';
    const base = (isClient ? ((window as any).__APP_BASE__ || '/') : '__PATHBASE__/').replace(/\/+$/, '');
    const mapSrc = base + '/images/world-dots.svg';

    const arcs = LINKS.map(([ox, oy, h], i) => {
      const [hx, hy] = HUBS[h];
      const mx = (ox + hx) / 2;
      const dist = Math.hypot(hx - ox, hy - oy);
      const cy = (oy + hy) / 2 - dist * 0.34; // lift the control point upward into an arc
      return {
        d: `M ${ox} ${oy} Q ${mx.toFixed(2)} ${cy.toFixed(2)} ${hx} ${hy}`,
        delay: 0.3 + i * 0.45,
      };
    });

    const nodes = [
      ...Object.values(HUBS).map(([x, y]) => ({ x, y, hub: true, delay: 0 })),
      ...LINKS.map(([x, y], i) => ({ x, y, hub: false, delay: 0.3 + i * 0.45 })),
    ];

    return { mapSrc, arcs, nodes };
  },
});
</script>

<style scoped>
.wm-hero {
  position: relative;
  width: 100%;
  max-width: 1100px;
}
.wm-map {
  position: relative;
  width: 100%;
  aspect-ratio: 158 / 112;
}
.wm-dots-img,
.wm-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.wm-dots-img {
  opacity: 0.5;
  object-fit: contain;
  user-select: none;
  /* Gentle top feather so the northernmost dots ease in */
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 7%);
  mask-image: linear-gradient(to bottom, transparent 0%, #000 7%);
  /* Pin to its own compositor layer. Without this the masked image is
     re-rasterized every scroll frame and flashes full-strength for one frame
     (the "purple flash"). Promoting caches the masked texture — no color change. */
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
}
.wm-overlay { overflow: visible; }

/* Connection arcs — draw from origin to the US hub, then loop */
.wm-arc {
  fill: none;
  stroke: rgba(var(--jb-primary-rgb), 0.95);
  stroke-width: 0.45;
  stroke-linecap: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: wm-draw 5s ease-in-out infinite;
}
@keyframes wm-draw {
  0%   { stroke-dashoffset: 1; opacity: 0; }
  10%  { opacity: 1; }
  55%  { stroke-dashoffset: 0; opacity: 1; }
  80%  { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}

/* Endpoint nodes + expanding ping rings */
.wm-node { fill: #fff; }
.wm-node--hub { fill: rgba(var(--jb-primary-rgb), 1); }
.wm-ring {
  fill: rgba(var(--jb-primary-rgb), 0.55);
  transform-box: fill-box;
  transform-origin: center;
  animation: wm-ping 3s ease-out infinite;
}
.wm-ring--hub { fill: rgba(var(--jb-primary-rgb), 0.85); }
@keyframes wm-ping {
  0%   { transform: scale(1); opacity: 0.7; }
  70%, 100% { transform: scale(3.2); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .wm-arc, .wm-ring { animation: none !important; }
  .wm-arc { stroke-dashoffset: 0; opacity: 0.6; }
}
</style>
